import type { PlatformImpact } from '../services/platformImpact';
import { usePlatformImpact } from '../hooks/usePlatformImpact';
import { useTranslation } from '../contexts/TranslationContext';
import { Star } from './Icons';
import { SignalTag } from './ui/SignalTag';
import { InfoTooltipButton } from './InfoTooltipButton';

interface AnswerRatingSignalProps {
  initialImpact?: PlatformImpact;
  className?: string;
}

export function AnswerRatingSignal({
  initialImpact,
  className = '',
}: AnswerRatingSignalProps) {
  const { t, language } = useTranslation();
  const { data } = usePlatformImpact(initialImpact);
  const rating = data?.answerRating;
  const ratingValue = rating?.value;
  const ratingCount = rating?.count || 0;
  const hasRating = typeof ratingValue === 'number' && ratingCount > 0;
  const locale = language === 'fr' ? 'fr-FR' : 'en-US';
  const formattedRating = hasRating
    ? `${ratingValue.toLocaleString(locale, { maximumFractionDigits: 2 })}/5`
    : '';
  const ratingCountLabel = hasRating
    ? t(ratingCount === 1 ? 'answerRating.countOne' : 'answerRating.countMany')
      .replace('{count}', ratingCount.toLocaleString(locale))
    : '';

  return (
    <span
      className={`inline-flex max-w-full transition-opacity duration-200 motion-reduce:transition-none ${
        hasRating ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      aria-live="polite"
    >
      {hasRating && (
        <SignalTag
          tone="rating"
          className={`relative overflow-visible text-center ${className}`}
          icon={<Star className="h-4 w-4 shrink-0 fill-current" aria-hidden="true" />}
        >
          <span>
            {t('answerRating.prefix')}{' '}
            <strong className="font-bold">{formattedRating}</strong>{' '}
            {t('answerRating.suffix')}
          </span>
          <span className="absolute -right-3 -top-3 inline-flex [&_button]:h-6 [&_button]:w-6 [&_button]:rounded-full [&_button]:text-[rgb(var(--signal-tag-accent))] [&_button:hover]:text-[rgb(var(--signal-tag-accent))]">
            <InfoTooltipButton
              compact
              label={ratingCountLabel}
              variant="plain"
              iconClassName="h-4 w-4 rounded-full bg-white p-0.5 shadow-sm"
            />
          </span>
        </SignalTag>
      )}
    </span>
  );
}
